import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { toast } from "react-toastify";

import { useDispatch, useSelector } from "react-redux";
import { fetchDriversDocumentsData } from "../../store/drivers-documents/actions";

import avatar2 from "../../assets/images/users/avatar-2.jpg";

const DriverDocuments = props => {
    const dispatch = useDispatch();

    const { id: driverId } = useParams();
    useEffect(() => {
        dispatch(fetchDriversDocumentsData(driverId));
    }, [dispatch, driverId]);

    const { data, loading, error } = useSelector(state => state.driversDocuments);

    const [selectedDriverDocuments, setSelectedDriverDocuments] = useState([]);
    const [selectedDocIds, setSelectedDocIds] = useState([]);

    const [previewDoc, setPreviewDoc] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const openPreviewModal = (doc) => {
        setPreviewDoc(doc);
        setShowPreviewModal(true);
    };

    const closePreviewModal = () => {
        setShowPreviewModal(false);
        setPreviewDoc(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "approved": return "success";
            case "rejected": return "danger";
            case "pending": return "warning";
            default: return "secondary";
        }
    };

    const isImage = (url) => {
        return /\.(jpeg|jpg|png|gif|bmp|webp)$/i.test(url);
    };

    const getFileExtension = (url) => {
        return url?.split('.').pop() || 'file';
    };

    const handleActionClick = async (action, driverId, docIds = []) => {
        let selectedIds = docIds.length > 0 ? docIds : selectedDocIds;

        if (selectedIds.length === 0 && selectedDriverDocuments?.documents?.length > 0) {
            selectedIds = selectedDriverDocuments.documents.map(doc => doc.id);
            setSelectedDocIds(selectedIds);
        }

        if (selectedIds.length === 0) {
            toast.error('Please select at least one document.', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        const payload = {
            action: action,
            document_ids: selectedIds,
            driver_id: driverId
        };

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(process.env.REACT_APP_WEB_APP_URL + "/api/web/update-drivers-documents", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.status == 200) {
                toast.success('Documents update successfully', {
                    position: "top-right",
                    autoClose: 3000,
                });

                setModalOpen(false);
                dispatch(fetchDriversDocumentsData(driverId));
            } else {
                toast.error(response.statusText, {
                    position: "top-right",
                    autoClose: 3000,
                });
            }

        } catch (error) {
            console.error("Error updating document status:", error);
            toast.error('Failed to update documents.', {
                position: "top-right",
                autoClose: 3000,
            });
        }
    };

    return (
        <div className="page-content">
            {loading ? (
                <Skeleton count={10} height={40} />
            ) : (
                <table className="table align-middle table-nowrap mb-0">
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((item, index) => {
                                if (item.documents?.length > 0) {
                                    return item.documents.map((doc, docIndex) => (
                                        <tr key={`doc-${item.id}-${docIndex}`}>
                                            <td style={{ width: "50px", cursor: "pointer" }} onClick={() => openPreviewModal(doc)}>
                                                {doc?.document && isImage(doc?.document) ? (
                                                    <img src={doc.document} className="rounded-circle avatar-sm" alt="" />
                                                ) : (
                                                    <div className="avatar-sm">
                                                        <span className="avatar-title rounded-circle bg-primary text-white font-size-14">
                                                            {doc?.document_name?.charAt(0)?.toUpperCase() || 'D'}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <h5 className="font-size-14 m-0">
                                                    <span className="text-dark">{doc?.document_name}</span>
                                                </h5>
                                            </td>
                                            <td>
                                                <span className={`badge bg-soft-${getStatusColor(doc.status)} text-${getStatusColor(doc.status)} font-size-13`}>
                                                    {doc.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        disabled={doc.status?.toLowerCase() === 'approved'}
                                                        onClick={() => handleActionClick('approved', item.driver_id, [doc.id])}
                                                    >
                                                        Approve
                                                    </button>

                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        disabled={doc.status?.toLowerCase() === 'rejected'}
                                                        onClick={() => handleActionClick('rejected', item.driver_id, [doc.id])}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ));
                                } else {
                                    return (
                                        <tr key={`no-doc-${item.id}`}>
                                            <td colSpan={4} className="text-center py-4">
                                                No documents available for this item
                                            </td>
                                        </tr>
                                    );
                                }
                            })
                        ) : (
                            <tr>
                                <td colSpan={4} className="text-center py-4">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {showPreviewModal && previewDoc && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {previewDoc.document_name} - {previewDoc.document_view}
                                </h5>
                                <button type="button" className="btn-close" onClick={closePreviewModal}></button>
                            </div>
                            <div className="modal-body text-center">
                                {isImage(previewDoc.document) ? (
                                    <img src={previewDoc.document} alt={previewDoc.document_name} style={{ maxWidth: '100%' }} />
                                ) : (
                                    <iframe
                                        src={previewDoc.document}
                                        title="Document Preview"
                                        style={{ width: '100%', height: '500px', border: 'none' }}
                                    ></iframe>
                                )}
                            </div>
                            <div className="modal-footer">
                                <span className={`badge bg-soft-${getStatusColor(previewDoc.status)} text-${getStatusColor(previewDoc.status)}`}>
                                    {previewDoc.status}
                                </span>
                                <button type="button" className="btn btn-secondary" onClick={closePreviewModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

DriverDocuments.propTypes = {
    t: PropTypes.any
};

export default DriverDocuments;